import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FileText, Upload, Key, X, Search, Loader, Calendar, Eye } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const InvoicesAdd = () => {
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [uploadType, setUploadType] = useState('ksef'); // 'ksef', 'pdf' lub 'xml'
  const [selectedFile, setSelectedFile] = useState(null);
  const [additionalAttachments, setAdditionalAttachments] = useState([]);
  const [isDraggingAttachments, setIsDraggingAttachments] = useState(false);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [manualAmounts, setManualAmounts] = useState(false); // Tryb ręcznego wprowadzania kwot
  
  // KSeF - automatyczne pobieranie faktur
  const [searchingKsef, setSearchingKsef] = useState(false);
  const [ksefInvoices, setKsefInvoices] = useState([]);
  const [ksefDateFrom, setKsefDateFrom] = useState('');
  const [ksefDateTo, setKsefDateTo] = useState('');
  const [ksefSubjectType, setKsefSubjectType] = useState('Subject2'); // Subject2 = faktury zakupowe
  const [importedKsefReferences, setImportedKsefReferences] = useState(new Set()); // Set zaimportowanych referencji KSeF
  const [selectedKsefInvoices, setSelectedKsefInvoices] = useState(new Set()); // Wielokrotny wybór do importu
  const [contractorResults, setContractorResults] = useState([]);
  const [contractorSearchLoading, setContractorSearchLoading] = useState(false);
  const [showContractorDropdown, setShowContractorDropdown] = useState(false);
  const contractorSearchRef = useRef(null);
  
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: {
      currency: 'PLN',
      status: 'NEW'
    }
  });

  // Obserwuj zmiany w kwocie netto i stawce VAT, aby automatycznie obliczyć VAT i brutto
  const amountNet = watch('amountNet');
  const vatRate = watch('vatRate');
  const vatAmount = watch('vatAmount');
  const amountGross = watch('amountGross');
  const supplierNameValue = watch('supplierName') || '';

  // Wyszukiwanie kontrahentów (debounced)
  useEffect(() => {
    if (!supplierNameValue || supplierNameValue.length < 2) {
      setContractorResults([]);
      setShowContractorDropdown(false);
      return;
    }
    const timer = setTimeout(async () => {
      setContractorSearchLoading(true);
      try {
        const res = await api.get('/contractors/search', { params: { q: supplierNameValue } });
        setContractorResults(res.data || []);
        setShowContractorDropdown((res.data || []).length > 0);
      } catch (err) {
        setContractorResults([]);
      } finally {
        setContractorSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [supplierNameValue]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (contractorSearchRef.current && !contractorSearchRef.current.contains(e.target)) {
        setShowContractorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
  }, []);

  // Ustaw domyślne daty dla KSeF (ostatnie 30 dni) i automatycznie pobierz faktury
  useEffect(() => {
    if (uploadType === 'ksef') {
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      const dateToStr = today.toISOString().split('T')[0] + 'T23:59:59';
      const dateFromStr = thirtyDaysAgo.toISOString().split('T')[0] + 'T00:00:00';
      
      setKsefDateTo(dateToStr);
      setKsefDateFrom(dateFromStr);
      
      // Automatycznie pobierz faktury po ustawieniu dat
      if (dateFromStr && dateToStr) {
        handleSearchKsefInvoices(dateFromStr, dateToStr, 'Subject2');
      }
    }
  }, [uploadType]);

  // Automatyczne obliczanie kwoty VAT i brutto (tylko gdy nie jest w trybie ręcznym)
  useEffect(() => {
    if (manualAmounts) {
      // W trybie ręcznym nie przeliczaj automatycznie
      return;
    }
    
    const net = parseFloat(amountNet) || 0;
    const rate = parseFloat(vatRate) || 0;
    
    if (net > 0 && rate >= 0) {
      // Oblicz kwotę VAT
      const calculatedVat = (net * rate) / 100;
      // Oblicz kwotę brutto
      const calculatedGross = net + calculatedVat;
      
      // Zawsze aktualizuj wartości (pola są readOnly, więc nie będzie konfliktów)
      setValue('vatAmount', calculatedVat.toFixed(2), { shouldValidate: false });
      setValue('amountGross', calculatedGross.toFixed(2), { shouldValidate: false });
    } else if (net === 0 || rate === 0) {
      // Jeśli netto lub stawka VAT jest 0, wyzeruj VAT i brutto
      setValue('vatAmount', '0.00', { shouldValidate: false });
      if (net === 0) {
        setValue('amountGross', '0.00', { shouldValidate: false });
      } else {
        setValue('amountGross', net.toFixed(2), { shouldValidate: false });
      }
    }
  }, [amountNet, vatRate, setValue, manualAmounts]);

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      // Nie pokazuj błędu użytkownikowi, jeśli to tylko lista działów
      // (może być pusta, jeśli nie ma działów)
      if (error.response?.status === 403) {
        console.warn('Brak uprawnień do pobrania listy działów');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users/assignment/list');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      // Nie pokazuj błędu użytkownikowi, jeśli to tylko lista użytkowników do przypisania
      // (może być pusta, jeśli nie ma aktywnych użytkowników)
      if (error.response?.status === 403) {
        console.warn('Brak uprawnień do pobrania listy użytkowników');
      }
    }
  };

  // Pobierz faktury z KSeF
  const handleSearchKsefInvoices = async (dateFrom, dateTo, subjectType) => {
    if (!dateFrom || !dateTo) {
      toast.error('Wybierz zakres dat');
      return;
    }
    
    setSearchingKsef(true);
    try {
      const response = await api.post('/ksef/invoices/metadata', {
        dateFrom,
        dateTo,
        pageOffset: 0,
        pageSize: 100,
        sortOrder: 'Desc', // Najnowsze pierwsze
        subjectType: subjectType || 'Subject2'
      });
      
      if (response.data.success) {
        const invoices = response.data.invoices || [];
        setKsefInvoices(invoices);
        
        // Sprawdź które faktury są już zaimportowane
        if (invoices.length > 0) {
          const ksefReferences = invoices.map(inv => inv.ksefNumber).filter(ref => ref);
          if (ksefReferences.length > 0) {
            try {
              const checkResponse = await api.post('/ksef/invoices/check-imported', {
                ksefReferences: ksefReferences
              });
              if (checkResponse.data.imported) {
                setImportedKsefReferences(new Set(checkResponse.data.imported));
              }
            } catch (error) {
              console.error('Błąd podczas sprawdzania zaimportowanych faktur:', error);
            }
          }
        }
        
        if (invoices.length > 0) {
          toast.success(`Znaleziono ${invoices.length} faktur z KSeF`);
        } else {
          toast.info('Nie znaleziono faktur w wybranym zakresie dat');
        }
      }
    } catch (error) {
      console.error('Błąd podczas pobierania faktur z KSeF:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas pobierania faktur z KSeF';
      toast.error(errorMessage);
      setKsefInvoices([]);
    } finally {
      setSearchingKsef(false);
    }
  };

  // Formatuj datę do wyświetlenia
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  // Wybierz fakturę z listy KSeF
  const handleSelectKsefInvoice = (invoice) => {
    if (importedKsefReferences.has(invoice.ksefNumber)) {
      toast.error('Ta faktura została już zaimportowana do systemu');
      return;
    }
    setValue('ksefReference', invoice.ksefNumber, { shouldValidate: false });
    if (invoice.sellerNip) {
      setValue('supplierNip', invoice.sellerNip, { shouldValidate: false });
    }
    toast.success(`Wybrano fakturę: ${invoice.invoiceNumber || invoice.ksefNumber}`);
  };

  const handleToggleKsefInvoiceCheckbox = (invoice, checked) => {
    if (importedKsefReferences.has(invoice.ksefNumber)) return;
    setSelectedKsefInvoices(prev => {
      const next = new Set(prev);
      if (checked) next.add(invoice.ksefNumber);
      else next.delete(invoice.ksefNumber);
      return next;
    });
  };

  const handleToggleAllKsefInvoices = (checked) => {
    if (checked) {
      const selectable = ksefInvoices.filter(inv => !importedKsefReferences.has(inv.ksefNumber));
      setSelectedKsefInvoices(new Set(selectable.map(inv => inv.ksefNumber)));
    } else {
      setSelectedKsefInvoices(new Set());
    }
  };

  // Podgląd faktury z KSeF
  const handlePreviewKsefInvoice = async (invoice) => {
    try {
      // Pobierz podgląd faktury z KSeF
      const response = await api.get(`/ksef/invoices/${invoice.ksefNumber}/preview`, {
        responseType: 'text'
      });
      
      // Otwórz podgląd w nowym oknie
      const newWindow = window.open('', '_blank', 'width=1200,height=800');
      if (newWindow) {
        newWindow.document.write(response.data);
        newWindow.document.close();
      }
    } catch (error) {
      console.error('Błąd podczas pobierania podglądu faktury z KSeF:', error);
      toast.error('Nie udało się pobrać podglądu faktury z KSeF');
    }
  };

  const validateAttachmentFile = (file) => {
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    return (allowedTypes.includes(file.type) || allowedExtensions.includes(ext)) && file.size <= 10 * 1024 * 1024;
  };

  const handleAttachmentsDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAttachments(true);
  };
  const handleAttachmentsDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAttachments(false);
  };
  const handleAttachmentsDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingAttachments(false);
    const files = Array.from(e.dataTransfer.files || []);
    const validFiles = files.filter(validateAttachmentFile);
    if (validFiles.length > 0) {
      setAdditionalAttachments(prev => [...prev, ...validFiles]);
    }
    if (files.length > 0 && validFiles.length < files.length) {
      toast.error('Niektóre pliki odrzucone. Dozwolone: PDF, PNG, JPG do 10MB.');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      const isXml = file.type === 'application/xml' || file.type === 'text/xml' || file.name.toLowerCase().endsWith('.xml');
      
      if (!isPdf && !isXml) {
        toast.error('Tylko pliki PDF lub XML są dozwolone');
        e.target.value = '';
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error('Plik jest zbyt duży. Maksymalny rozmiar to 10MB');
        e.target.value = '';
        return;
      }
      setSelectedFile(file);
      
      // Automatyczne parsowanie PDF lub XML
      setParsing(true);
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const endpoint = isXml ? '/invoices/parse/xml' : '/invoices/parse/pdf';
        const response = await api.post(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        const parsedData = response.data;
        
        // Loguj otrzymane dane dla debugowania
        console.log('Otrzymane dane z parsowania:', parsedData);
        
        // Wypełnianie formularza danymi z faktury
        // Używamy sprawdzenia na undefined/null, ale pozwalamy na puste stringi
        if (parsedData.number !== undefined && parsedData.number !== null) {
          setValue('number', String(parsedData.number).trim(), { shouldValidate: false });
        }
        if (parsedData.supplierName !== undefined && parsedData.supplierName !== null) {
          setValue('supplierName', String(parsedData.supplierName).trim(), { shouldValidate: false });
        }
        if (parsedData.supplierNip !== undefined && parsedData.supplierNip !== null) {
          setValue('supplierNip', String(parsedData.supplierNip).trim(), { shouldValidate: false });
        }
        if (parsedData.issueDate !== undefined && parsedData.issueDate !== null) {
          // Konwertuj datę do formatu YYYY-MM-DD jeśli potrzeba
          let dateStr = String(parsedData.issueDate).trim();
          // Jeśli data jest w formacie DD.MM.YYYY, konwertuj do YYYY-MM-DD
          if (dateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
            const [day, month, year] = dateStr.split('.');
            dateStr = `${year}-${month}-${day}`;
          }
          setValue('issueDate', dateStr, { shouldValidate: false });
        }
        if (parsedData.dueDate !== undefined && parsedData.dueDate !== null) {
          // Konwertuj datę do formatu YYYY-MM-DD jeśli potrzeba
          let dateStr = String(parsedData.dueDate).trim();
          // Jeśli data jest w formacie DD.MM.YYYY, konwertuj do YYYY-MM-DD
          if (dateStr.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
            const [day, month, year] = dateStr.split('.');
            dateStr = `${year}-${month}-${day}`;
          }
          setValue('dueDate', dateStr, { shouldValidate: false });
        }
        // Jeśli plik zawiera kwoty, automatycznie włącz tryb ręczny, aby zachować wartości z pliku
        const hasAmounts = (parsedData.amountNet !== undefined && parsedData.amountNet !== null) ||
                          (parsedData.vatAmount !== undefined && parsedData.vatAmount !== null) ||
                          (parsedData.amountGross !== undefined && parsedData.amountGross !== null);
        
        if (hasAmounts) {
          // Włącz tryb ręczny, aby zachować wartości z pliku (mogą nie pasować do automatycznego przeliczania)
          setManualAmounts(true);
        }
        
        if (parsedData.amountNet !== undefined && parsedData.amountNet !== null) {
          const amount = parseFloat(parsedData.amountNet);
          if (!isNaN(amount)) {
            setValue('amountNet', amount.toFixed(2), { shouldValidate: false });
          }
        }
        if (parsedData.vatRate !== undefined && parsedData.vatRate !== null) {
          const rate = parseFloat(parsedData.vatRate);
          if (!isNaN(rate) && rate >= 0) {
            setValue('vatRate', rate.toFixed(2), { shouldValidate: false });
          }
        }
        if (parsedData.vatAmount !== undefined && parsedData.vatAmount !== null) {
          const amount = parseFloat(parsedData.vatAmount);
          if (!isNaN(amount) && amount >= 0) {
            setValue('vatAmount', amount.toFixed(2), { shouldValidate: false });
          }
        }
        if (parsedData.amountGross !== undefined && parsedData.amountGross !== null) {
          const amount = parseFloat(parsedData.amountGross);
          if (!isNaN(amount)) {
            setValue('amountGross', amount.toFixed(2), { shouldValidate: false });
          }
        }
        if (parsedData.currency !== undefined && parsedData.currency !== null) {
          setValue('currency', String(parsedData.currency).trim().toUpperCase(), { shouldValidate: false });
        }
        
        // Sprawdź ile pól zostało wypełnionych
        const filledFields = Object.keys(parsedData).filter(key => 
          parsedData[key] !== undefined && parsedData[key] !== null && String(parsedData[key]).trim() !== ''
        ).length;
        
        if (filledFields > 0) {
          toast.success(`Dane z faktury ${isXml ? 'XML' : 'PDF'} zostały automatycznie wczytane (${filledFields} pól)`);
        } else {
          toast(`Nie udało się odczytać danych z faktury ${isXml ? 'XML' : 'PDF'}. Wypełnij formularz ręcznie.`, {
            icon: '⚠️',
            duration: 5000
          });
        }
      } catch (error) {
        console.error(`Błąd podczas parsowania ${isXml ? 'XML' : 'PDF'}:`, error);
        const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Nieznany błąd';
        console.error('Szczegóły błędu:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          url: error.config?.url
        });
        
        if (error.response?.status === 404) {
          toast.error('Endpoint parsowania nie jest dostępny. Upewnij się, że backend został zrestartowany.');
        } else {
          toast.error(`Nie udało się automatycznie odczytać danych z faktury: ${errorMessage}. Możesz wypełnić formularz ręcznie.`);
        }
      } finally {
        setParsing(false);
      }
    }
  };

  const onSubmitKsef = async (data) => {
    const invoicesToImport = selectedKsefInvoices.size > 0
      ? ksefInvoices.filter(inv => selectedKsefInvoices.has(inv.ksefNumber))
      : (data.ksefReference ? [{ ksefNumber: data.ksefReference, sellerNip: data.supplierNip }] : []);

    if (invoicesToImport.length === 0) {
      toast.error('Wybierz fakturę z listy lub zaznacz kilka faktur do importu');
      return;
    }

    setLoading(true);
    try {
      let successCount = 0;
      const throttleMs = 3000; // 1 request / 3 sekundy - ochrona limitów KSeF
      for (let i = 0; i < invoicesToImport.length; i++) {
        const invoice = invoicesToImport[i];
        if (i > 0) {
          await new Promise(r => setTimeout(r, throttleMs));
        }
        const ksefRef = typeof invoice === 'object' ? invoice.ksefNumber : invoice;
        const supplierNip = typeof invoice === 'object' ? invoice.sellerNip : data.supplierNip;
        try {
          const metadata = typeof invoice === 'object' ? invoice : null;
          const response = await api.post('/invoices/import/ksef', {
            ksefReference: ksefRef,
            supplierNip: supplierNip || data.supplierNip,
            metadata: metadata
          });
          if (!response?.data) throw new Error('Brak odpowiedzi z serwera');
          const invoiceId = response.data?.id;
          if (additionalAttachments.length > 0 && invoiceId) {
            for (const file of additionalAttachments) {
              const attachmentFormData = new FormData();
              attachmentFormData.append('file', file);
              await api.post(`/invoices/${invoiceId}/attachments`, attachmentFormData, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
            }
          }
          successCount++;
          setImportedKsefReferences(prev => new Set([...prev, ksefRef]));
          setSelectedKsefInvoices(prev => {
            const next = new Set(prev);
            next.delete(ksefRef);
            return next;
          });
        } catch (err) {
          const msg = err.response?.data?.error || err.response?.data?.message || err.message;
          toast.error(`Błąd importu ${ksefRef}: ${msg}`);
        }
      }
      if (successCount > 0) {
        toast.success(successCount === 1
          ? (additionalAttachments.length > 0 ? 'Faktura została zaimportowana z KSeF wraz z załącznikami' : 'Faktura została zaimportowana z KSeF')
          : `Zaimportowano ${successCount} faktur z KSeF`);
        reset();
        setAdditionalAttachments([]);
        navigate('/app/invoices/list');
      }
    } catch (error) {
      console.error('Błąd podczas importu faktury z KSeF:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || error.message || 'Błąd podczas importu faktury z KSeF');
    } finally {
      setLoading(false);
    }
  };

  const onSubmitPdf = async (data) => {
    if (!selectedFile) {
      toast.error('Proszę wybrać plik PDF lub XML');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (data.number) formData.append('number', data.number);
      if (data.supplierName) formData.append('supplierName', data.supplierName);
      if (data.supplierNip) formData.append('supplierNip', data.supplierNip);
      if (data.issueDate) formData.append('issueDate', data.issueDate);
      if (data.dueDate) formData.append('dueDate', data.dueDate);
      if (data.amountNet) formData.append('amountNet', data.amountNet);
      if (data.vatRate) formData.append('vatRate', data.vatRate);
      if (data.vatAmount) formData.append('vatAmount', data.vatAmount);
      if (data.amountGross) formData.append('amountGross', data.amountGross);
      if (data.currency) formData.append('currency', data.currency);

      const isXml = selectedFile.type === 'application/xml' || selectedFile.type === 'text/xml' || selectedFile.name.toLowerCase().endsWith('.xml');
      const endpoint = isXml ? '/invoices/upload/xml' : '/invoices/upload/pdf';
      
      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const invoiceId = response.data?.id;
      
      // Jeśli są dodatkowe załączniki, dodaj je
      if (additionalAttachments.length > 0 && invoiceId) {
        setUploadingAttachments(true);
        try {
          for (const file of additionalAttachments) {
            const attachmentFormData = new FormData();
            attachmentFormData.append('file', file);
            await api.post(`/invoices/${invoiceId}/attachments`, attachmentFormData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          }
          toast.success(`Faktura została dodana z pliku ${isXml ? 'XML' : 'PDF'} wraz z załącznikami`);
        } catch (error) {
          console.error('Błąd podczas dodawania załączników:', error);
          toast.success(`Faktura została dodana z pliku ${isXml ? 'XML' : 'PDF'}, ale wystąpił błąd podczas dodawania załączników`);
        } finally {
          setUploadingAttachments(false);
        }
      } else {
      toast.success(`Faktura została dodana z pliku ${isXml ? 'XML' : 'PDF'}`);
      }
      
      reset();
      setSelectedFile(null);
      setAdditionalAttachments([]);
      navigate('/app/invoices/list');
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Błąd podczas dodawania faktury';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-xl">Dodaj fakturę</h2>
      </div>
      <div className="card-body">
        {/* Przełącznik typu uploadu */}
        <div className="mb-6">
          <div className="flex space-x-4 border-b border-gray-200">
            <button
              type="button"
              onClick={() => {
                setUploadType('ksef');
                reset();
                setSelectedFile(null);
                setManualAmounts(false); // Resetuj tryb ręczny przy zmianie typu uploadu
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                uploadType === 'ksef'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center">
                <Key className="h-4 w-4 mr-2" />
                Import z KSeF
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setUploadType('pdf');
                reset();
                setSelectedFile(null);
              }}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                uploadType === 'pdf'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className="flex items-center">
                <Upload className="h-4 w-4 mr-2" />
                Upload pliku PDF/XML
              </div>
            </button>
          </div>
        </div>

        {uploadType === 'ksef' ? (
          <div className="space-y-5">
            {/* Wyszukiwanie faktur KSeF */}
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Data od</label>
                <input
                  type="datetime-local"
                  value={ksefDateFrom}
                  onChange={(e) => setKsefDateFrom(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Data do</label>
                <input
                  type="datetime-local"
                  value={ksefDateTo}
                  onChange={(e) => setKsefDateTo(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Typ faktur</label>
                <select
                  value={ksefSubjectType}
                  onChange={(e) => setKsefSubjectType(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="Subject2">Faktury zakupowe</option>
                  <option value="Subject1">Faktury sprzedażowe</option>
                </select>
              </div>
              <button
                type="button"
                onClick={() => handleSearchKsefInvoices(ksefDateFrom, ksefDateTo, ksefSubjectType)}
                disabled={searchingKsef || !ksefDateFrom || !ksefDateTo}
                className="px-5 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {searchingKsef ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Szukanie...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4" />
                    Wyszukaj
                  </>
                )}
              </button>
            </div>

            {/* Lista faktur z KSeF */}
            {ksefInvoices.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                  <span className="text-sm font-semibold text-gray-800">
                    Znalezione faktury: {ksefInvoices.length}
                  </span>
                </div>
                <div className="max-h-72 overflow-y-auto overflow-x-hidden">
                  <table className="w-full divide-y divide-gray-200 table-fixed">
                    <colgroup>
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '4%' }} />
                      <col style={{ width: '4%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '9%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '7%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '4%' }} />
                    </colgroup>
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Wybierz</th>
                          <th className="px-1 py-1.5 text-center text-[10px] font-medium text-gray-500 uppercase">Podgląd</th>
                          <th className="px-1 py-1.5 text-center text-[10px] font-medium text-gray-500 uppercase">
                            <input
                              type="checkbox"
                              checked={ksefInvoices.filter(inv => !importedKsefReferences.has(inv.ksefNumber)).length > 0 &&
                                selectedKsefInvoices.size === ksefInvoices.filter(inv => !importedKsefReferences.has(inv.ksefNumber)).length}
                              onChange={(e) => handleToggleAllKsefInvoices(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                              title="Zaznacz wszystkie"
                            />
                          </th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Nr KSeF</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Nr faktury</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Sprzedawca</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">NIP</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Data wyst.</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Data KSeF</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Data otrz.</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Netto</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">Brutto</th>
                          <th className="px-1 py-1.5 text-left text-[10px] font-medium text-gray-500 uppercase">VAT</th>
                        </tr>
                      </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {ksefInvoices.map((invoice, index) => {
                        const isImported = importedKsefReferences.has(invoice.ksefNumber);
                        const isChecked = selectedKsefInvoices.has(invoice.ksefNumber);
                        return (
                        <tr 
                          key={index} 
                          className={`hover:bg-gray-50 ${isImported ? 'bg-green-50' : ''} ${!isImported ? 'cursor-pointer' : ''}`}
                          onClick={() => !isImported && handleSelectKsefInvoice(invoice)}
                        >
                          <td className="px-1 py-1.5">
                            {!isImported ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSelectKsefInvoice(invoice);
                                }}
                                className="px-2 py-0.5 text-[10px] text-white bg-primary-600 rounded hover:bg-primary-700"
                              >
                                Wybierz
                              </button>
                            ) : (
                              <span className="px-1.5 py-0.5 text-[10px] text-green-700 bg-green-100 rounded font-semibold" title="Zaimportowana">
                                Imp.
                              </span>
                            )}
                          </td>
                          <td className="px-1 py-1.5 text-center">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewKsefInvoice(invoice);
                              }}
                              className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                              title="Podgląd faktury z KSeF"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                          </td>
                          <td className="px-1 py-1.5 text-center">
                            {!isImported ? (
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleToggleKsefInvoiceCheckbox(invoice, e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                title="Zaznacz do importu"
                              />
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-1 py-1.5 text-xs font-mono min-w-0 overflow-hidden" title={invoice.ksefNumber || ''}>
                            <span className="block truncate">{invoice.ksefNumber || '-'}</span>
                          </td>
                          <td className="px-1 py-1.5 text-xs font-mono min-w-0 overflow-hidden" title={invoice.invoiceNumber || ''}>
                            <span className="block truncate">{invoice.invoiceNumber || '-'}</span>
                          </td>
                          <td className="px-1 py-1.5 text-xs min-w-0 overflow-hidden" title={invoice.sellerName || ''}>
                            <span className="block truncate">{invoice.sellerName || '-'}</span>
                          </td>
                          <td className="px-1 py-1.5 text-xs tabular-nums">{invoice.sellerNip || '-'}</td>
                          <td className="px-1 py-1.5 text-xs tabular-nums">{formatDate(invoice.issueDate)}</td>
                          <td className="px-1 py-1.5 text-xs tabular-nums">{formatDate(invoice.permanentStorageDate)}</td>
                          <td className="px-1 py-1.5 text-xs tabular-nums">{formatDate(invoice.invoicingDate)}</td>
                          <td className="px-1 py-1.5 text-xs tabular-nums">{invoice.netAmount != null ? `${invoice.netAmount.toFixed(2)}` : '-'}</td>
                          <td className="px-1 py-1.5 text-xs tabular-nums">{invoice.grossAmount != null ? `${invoice.grossAmount.toFixed(2)}` : '-'}</td>
                          <td className="px-1 py-1.5 text-xs tabular-nums">{invoice.vatAmount != null ? `${invoice.vatAmount.toFixed(2)}` : '-'}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Formularz importu z wybraną fakturą */}
            <form onSubmit={handleSubmit(onSubmitKsef)} className="space-y-6 border-t border-gray-200 pt-6">
              <div>
                <label htmlFor="ksefReference" className="block text-sm text-gray-700 mb-2">
                  Referencja KSeF <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('ksefReference')}
                  type="text"
                  id="ksefReference"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Wybierz fakturę z listy powyżej lub wprowadź referencję ręcznie"
                />
                {errors.ksefReference && (
                  <p className="mt-1 text-sm text-red-600">{errors.ksefReference.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="supplierNip" className="block text-sm text-gray-700 mb-2">
                  NIP dostawcy
                </label>
                <input
                  {...register('supplierNip', {
                    setValueAs: (v) => (v ? v.toString().replace(/\s+/g, '').toUpperCase() : v),
                    pattern: {
                      value: /^$|^[0-9]{10}$|^[A-Za-z]{2}[A-Za-z0-9]{2,13}$/,
                      message: 'NIP musi mieć 10 cyfr lub być zagranicznym numerem VAT (np. EU521412356)'
                    }
                  })}
                  type="text"
                  id="supplierNip"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 uppercase"
                  placeholder="1234567890 lub EU521412356"
                  maxLength="15"
                />
                {errors.supplierNip && (
                  <p className="mt-1 text-sm text-red-600">{errors.supplierNip.message}</p>
                )}
              </div>

            {/* Sekcja załączników */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Dodatkowe załączniki (PDF, PNG, JPG) - opcjonalnie
              </label>
              <div className="space-y-3">
                <div
                  className={`flex justify-center items-center px-4 py-4 border-2 border-dashed rounded-lg transition-colors cursor-pointer min-h-[80px] ${
                    isDraggingAttachments ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
                  }`}
                  onDragOver={handleAttachmentsDragOver}
                  onDragLeave={handleAttachmentsDragLeave}
                  onDrop={handleAttachmentsDrop}
                  onClick={() => document.getElementById('additional-attachments-ksef')?.click()}
                >
                  <input
                    type="file"
                    id="additional-attachments-ksef"
                    accept=".pdf,.png,.jpg,.jpeg"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const validFiles = files.filter(validateAttachmentFile);
                      setAdditionalAttachments(prev => [...prev, ...validFiles]);
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Upload className="h-4 w-4" />
                    <span>Przeciągnij pliki tutaj lub kliknij aby wybrać</span>
                  </div>
                </div>
                
                {/* Lista załączników */}
                {additionalAttachments.length > 0 && (
                  <div className="space-y-2">
                    {additionalAttachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate" title={file.name}>
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setAdditionalAttachments(prev => prev.filter((_, i) => i !== index));
                          }}
                          className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Usuń"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

              <div className="flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate('/app/invoices/list')}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={loading || uploadingAttachments}
                  className="px-4 py-2 text-sm text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading || uploadingAttachments ? (
                    <span className="flex items-center">
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">{uploadingAttachments ? 'Dodawanie załączników...' : 'Importowanie...'}</span>
                    </span>
                  ) : (
                    'Importuj z KSeF'
                  )}
                </button>
              </div>
            </form>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmitPdf)} className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Upload className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm text-green-800">Upload pliku PDF/XML</h3>
                  <p className="mt-1 text-sm text-green-700">
                    Prześlij plik PDF, XML (KSeF) lub skan faktury. Możesz opcjonalnie uzupełnić dane faktury ręcznie.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="file" className="block text-sm text-gray-700 mb-2">
                Plik PDF/XML <span className="text-red-500">*</span>
              </label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                  <div className="space-y-1 text-center">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  {parsing ? (
                    <div className="flex flex-col items-center">
                      <LoadingSpinner size="sm" />
                      <p className="mt-2 text-sm text-gray-600">Odczytywanie danych z faktury...</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex text-sm text-gray-600">
                        <label htmlFor="file" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                          <span>Wybierz plik</span>
                          <input
                            id="file"
                            name="file"
                            type="file"
                            accept=".pdf,application/pdf,.xml,application/xml,text/xml"
                            onChange={handleFileChange}
                            className="sr-only"
                            disabled={parsing}
                          />
                        </label>
                        <p className="pl-1">lub przeciągnij i upuść</p>
                      </div>
                      <p className="text-xs text-gray-500">PDF lub XML do 10MB</p>
                      {selectedFile && (
                        <p className="text-sm text-green-600 mt-2">
                          Wybrany plik: {selectedFile.name}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="number" className="block text-sm text-gray-700 mb-2">
                  Numer faktury
                </label>
                <input
                  {...register('number')}
                  type="text"
                  id="number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="FV/2024/001"
                />
              </div>

              <div className="relative" ref={contractorSearchRef}>
                <label htmlFor="supplierName" className="block text-sm text-gray-700 mb-2">
                  Nazwa dostawcy
                </label>
                <div className="relative">
                  <input
                    {...register('supplierName')}
                    type="text"
                    id="supplierName"
                    className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Wpisz lub wyszukaj firmę z bazy"
                    autoComplete="off"
                    onFocus={() => contractorResults.length > 0 && setShowContractorDropdown(true)}
                  />
                  {contractorSearchLoading && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <Loader className="h-4 w-4 animate-spin text-gray-400" />
                    </div>
                  )}
                </div>
                {showContractorDropdown && contractorResults.length > 0 && (
                  <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                    {contractorResults.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 flex flex-col"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setValue('supplierName', c.name, { shouldValidate: false });
                            const nipVal = (c.nip || '').startsWith('EXT') ? '' : (c.nip || '');
                            setValue('supplierNip', nipVal, { shouldValidate: false });
                            setShowContractorDropdown(false);
                            setContractorResults([]);
                          }}
                        >
                          <span className="font-medium text-gray-900">{c.name}</span>
                          {c.nip && !c.nip.startsWith('EXT') && (
                            <span className="text-xs text-gray-500">NIP: {c.nip}</span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="supplierNip" className="block text-sm text-gray-700 mb-2">
                  NIP dostawcy
                </label>
                <input
                  {...register('supplierNip', {
                    setValueAs: (v) => (v ? v.toString().replace(/\s+/g, '').toUpperCase() : v),
                    pattern: {
                      value: /^$|^[0-9]{10}$|^[A-Za-z]{2}[A-Za-z0-9]{2,13}$/,
                      message: 'NIP musi mieć 10 cyfr lub być zagranicznym numerem VAT (np. EU521412356)'
                    }
                  })}
                  type="text"
                  id="supplierNip"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 uppercase"
                  placeholder="1234567890 lub EU521412356"
                  maxLength="15"
                />
                {errors.supplierNip && (
                  <p className="mt-1 text-sm text-red-600">{errors.supplierNip.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="currency" className="block text-sm text-gray-700 mb-2">
                  Waluta
                </label>
                <select
                  {...register('currency')}
                  id="currency"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="PLN">PLN</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="UAH">UAH (грн)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="issueDate" className="block text-sm text-gray-700 mb-2">
                  Data wystawienia
                </label>
                <input
                  {...register('issueDate')}
                  type="date"
                  id="issueDate"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label htmlFor="dueDate" className="block text-sm text-gray-700 mb-2">
                  Termin płatności
                </label>
                <input
                  {...register('dueDate')}
                  type="date"
                  id="dueDate"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Sekcja kwot z opcją ręcznego wprowadzania */}
            <div className="space-y-4">
              {/* Opcja ręcznego wprowadzania kwot */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="manualAmounts"
                    checked={manualAmounts}
                    onChange={(e) => {
                      setManualAmounts(e.target.checked);
                      if (!e.target.checked) {
                        // Gdy wyłączamy tryb ręczny, przelicz automatycznie
                        const net = parseFloat(amountNet) || 0;
                        const rate = parseFloat(vatRate) || 0;
                        if (net > 0 && rate >= 0) {
                          const calculatedVat = (net * rate) / 100;
                          const calculatedGross = net + calculatedVat;
                          setValue('vatAmount', calculatedVat.toFixed(2), { shouldValidate: false });
                          setValue('amountGross', calculatedGross.toFixed(2), { shouldValidate: false });
                        }
                      }
                    }}
                    className="h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer mt-0.5 flex-shrink-0"
                  />
                  <label htmlFor="manualAmounts" className="text-sm font-medium text-gray-700 cursor-pointer flex-1">
                    <span className="font-semibold text-blue-800 block mb-1">Wprowadź kwoty ręcznie</span>
                    <span className="block text-xs text-gray-600">
                      {manualAmounts 
                        ? "✓ Tryb ręczny włączony - możesz wprowadzić kwoty netto, VAT i brutto bez automatycznego przeliczania"
                        : "Kwoty VAT i brutto są obliczane automatycznie na podstawie kwoty netto i stawki VAT"}
                    </span>
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="amountNet" className="block text-sm text-gray-700 mb-2">
                    Kwota netto
                  </label>
                <input
                  {...register('amountNet', {})}
                  type="number"
                  step="0.01"
                  id="amountNet"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="0.00"
                />
                {errors.amountNet && (
                  <p className="mt-1 text-sm text-red-600">{errors.amountNet.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="vatRate" className="block text-sm text-gray-700 mb-2">
                  Stawka VAT (%)
                </label>
                <input
                  {...register('vatRate', {
                    min: {
                      value: 0,
                      message: 'Stawka VAT nie może być ujemna'
                    },
                    max: {
                      value: 100,
                      message: 'Stawka VAT nie może przekraczać 100%'
                    }
                  })}
                  type="number"
                  step="0.01"
                  id="vatRate"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="23.00"
                  disabled={manualAmounts}
                  title={manualAmounts ? "W trybie ręcznym stawka VAT nie jest używana do przeliczania" : ""}
                />
                {errors.vatRate && (
                  <p className="mt-1 text-sm text-red-600">{errors.vatRate.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="vatAmount" className="block text-sm text-gray-700 mb-2">
                  Kwota VAT
                </label>
                <input
                  {...register('vatAmount', {
                    min: {
                      value: 0,
                      message: 'Kwota VAT nie może być ujemna'
                    }
                  })}
                  type="number"
                  step="0.01"
                  id="vatAmount"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${manualAmounts ? 'bg-white' : 'bg-gray-50'}`}
                  placeholder="0.00"
                  readOnly={!manualAmounts}
                  title={manualAmounts ? "Wprowadź kwotę VAT ręcznie" : "Obliczane automatycznie na podstawie kwoty netto i stawki VAT"}
                />
                {errors.vatAmount && (
                  <p className="mt-1 text-sm text-red-600">{errors.vatAmount.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="amountGross" className="block text-sm text-gray-700 mb-2">
                  Kwota brutto
                </label>
                <input
                  {...register('amountGross', {})}
                  type="number"
                  step="0.01"
                  id="amountGross"
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${manualAmounts ? 'bg-white' : 'bg-gray-50'}`}
                  placeholder="0.00"
                  readOnly={!manualAmounts}
                  title={manualAmounts ? "Wprowadź kwotę brutto ręcznie" : "Obliczane automatycznie na podstawie kwoty netto i kwoty VAT"}
                />
                {errors.amountGross && (
                  <p className="mt-1 text-sm text-red-600">{errors.amountGross.message}</p>
                )}
              </div>
            </div>
            </div>

            {/* Sekcja załączników */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Dodatkowe załączniki (PDF, PNG, JPG) - opcjonalnie
              </label>
              <div className="space-y-3">
                <div
                  className={`flex justify-center items-center px-4 py-4 border-2 border-dashed rounded-lg transition-colors cursor-pointer min-h-[80px] ${
                    isDraggingAttachments ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50/50'
                  }`}
                  onDragOver={handleAttachmentsDragOver}
                  onDragLeave={handleAttachmentsDragLeave}
                  onDrop={handleAttachmentsDrop}
                  onClick={() => document.getElementById('additional-attachments')?.click()}
                >
                  <input
                    type="file"
                    id="additional-attachments"
                    accept=".pdf,.png,.jpg,.jpeg"
                    multiple
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      const validFiles = files.filter(validateAttachmentFile);
                      setAdditionalAttachments(prev => [...prev, ...validFiles]);
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Upload className="h-4 w-4" />
                    <span>Przeciągnij pliki tutaj lub kliknij aby wybrać</span>
                  </div>
                </div>
                
                {/* Lista załączników */}
                {additionalAttachments.length > 0 && (
                  <div className="space-y-2">
                    {additionalAttachments.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                          <span className="text-sm text-gray-700 truncate" title={file.name}>
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setAdditionalAttachments(prev => prev.filter((_, i) => i !== index));
                          }}
                          className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Usuń"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate('/app/invoices/list')}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={loading || uploadingAttachments || !selectedFile}
                className="px-4 py-2 text-sm text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {(loading || uploadingAttachments) ? (
                  <span className="flex items-center">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">{uploadingAttachments ? 'Dodawanie załączników...' : 'Przesyłanie...'}</span>
                  </span>
                ) : (
                  'Dodaj fakturę'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default InvoicesAdd;
